import React, { SetStateAction } from "react"
import { useTranslation } from "react-i18next"
import clsx from "clsx"

import * as BikeApi from "features/bike/api/BikeApi"

import { useAlert, useForm, useUser } from "hooks"
import {
	BIKE_STATUS_IN_SALE,
	checkIsCompanyConsumer,
	checkIsPrivateConsumer,
	consumers,
	PRIVATE_GENDER_VALUE,
} from "utils"
import { FormElement, formStatusEnum } from "components/Form"
import { Bike, ErrorResponse } from "models"
import { Button, Spinner } from "components/Elements"

const defaultFormFields = {
	gender: PRIVATE_GENDER_VALUE.toString(),
	first_name: "",
	last_name: "",
	social_reason: "",
	phone: "",
	mail: "",
	confirmMail: "",
}

type SellFormProps = {
	bike: Bike
	setModalIsOpen: React.Dispatch<SetStateAction<boolean>>
	refreshBike?: () => Promise<void>
}

const ADD_TRANSFER = "ADD_TRANSFER"
const CANCEL_TRANSFER = "CANCEL_TRANSFER"

export const SellForm: React.FC<SellFormProps> = ({ bike, setModalIsOpen, refreshBike }) => {
	const {
		formFields,
		formStatus,
		setFormStatus,
		handleChange,
		errors,
		showLocalErrors,
		setShowLocalErrors,
		handleValidation,
	} = useForm({
		defaultFormFields: bike.bike_in_transit ? bike.bike_owner : defaultFormFields,
		defaultFormStatus: formStatusEnum.ready,
	})

	const { t } = useTranslation()
	const { alert } = useAlert()
	const { user } = useUser()

	const isLoading = formStatus === formStatusEnum.loading
	const isReady = formStatus === formStatusEnum.ready

	const isCurrentlyInTransit = bike.bike_in_transit
	const isBikeInSale = bike.bike_status === BIKE_STATUS_IN_SALE

	const isPrivateUser = checkIsPrivateConsumer({
		gender: formFields.gender,
	})

	const isCompany = checkIsCompanyConsumer({
		gender: formFields.gender,
	})

	const consumersOptions = consumers.map((consumer) => ({
		...consumer,
		value: consumer.value.toString(),
	}))

	const handleSubmit = async (updateType: string) => {
		// Validate inputs
		const formReady = !(errors.length > 0)
		const isCancelTransfer = updateType === CANCEL_TRANSFER

		// Lors de l'annulation d'un transfert, on va envoyer les informations du propriétaire actuel (currentBikeOwner)
		// pour écraser les infos précédemment envoyées (newBikeOwner)
		// Pas besoin de vérifier les champs car on ne les prendra pas en compte dans ce cas précis
		if (!formReady && !isCancelTransfer) {
			setShowLocalErrors(true)
		} else {
			try {
				// prevent form to be submitted again
				setFormStatus(formStatusEnum.loading)
				const newBikeOwner = {
					gender: parseInt(formFields.gender),
					phone: formFields.phone.trim(),
					mail: formFields.mail.trim(),
					...(() => {
						if (parseInt(formFields.gender) === PRIVATE_GENDER_VALUE) {
							return {
								first_name: formFields.first_name.trim(),
								last_name: formFields.last_name.trim(),
							}
						} else {
							return {
								social_reason: formFields.social_reason.trim(),
							}
						}
					})(),
				}

				const currentBikeOwner = {
					gender: user.description.gender,
					phone: user.description.phone,
					mail: user.description.mail,
					...(() => {
						if (user.description.gender === PRIVATE_GENDER_VALUE) {
							return {
								first_name: user.description.first_name,
								last_name: user.description.last_name,
							}
						} else {
							return {
								social_reason: user.description.social_reason,
							}
						}
					})(),
				}

				const updatedBikeOwnerPayload = {
					bike_id: bike.bike_id,
					row_version: bike.row_version,
					new_owner: isCancelTransfer ? currentBikeOwner : newBikeOwner,
					...(isCancelTransfer ? { cancel_transfer: true } : {}),
				}

				const { getError } = await BikeApi.modifyBikeProperty(updatedBikeOwnerPayload)
				if (getError()) throw getError()
				alert(
					"success",
					isCancelTransfer
						? t("bike_state.sell_modal_cancel_confirm_success", {
								ns: "bike",
						  })
						: t("bike_state.sell_modal_confirm_success", {
								ns: "bike",
						  })
				)
				await refreshBike()
			} catch (error) {
				console.error(error)
				const errorMsg = (error as ErrorResponse)?.message?.toString()
				alert("error", errorMsg)
			} finally {
				setFormStatus(formStatusEnum.ready)
			}
		}
	}

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleChange,
	}

	return (
		<form className="lg:px-20">
			{!isCurrentlyInTransit && (
				<FormElement
					{...formElementCommonProps}
					value={formFields.gender}
					name="gender"
					type="radio"
					options={consumersOptions}
					classNameOptionsContainer="flex"
					classNameOption="ml-2"
					className="mb-1"
					required={true}
				/>
			)}
			{isCompany && !isCurrentlyInTransit && (
				<>
					{!isBikeInSale && (
						<p className="py-1 text-xxs text-center">
							{t("bike_state.sell_modal_bicycle_dealer_description", {
								ns: "bike",
							})}
						</p>
					)}

					{isBikeInSale && (
						<p className="py-1 text-xxs text-center">
							{t("bike_state.sell_modal_bicycle_dealer_insale_bike_description", {
								ns: "bike",
							})}
						</p>
					)}
				</>
			)}

			{isPrivateUser && (
				<div className="flex justify-between flex-wrap">
					<FormElement
						{...formElementCommonProps}
						value={formFields.last_name}
						name="last_name"
						id="last_name"
						type="text"
						label={t("lastname", { ns: "user" })}
						className="my-1 w-full sm:w-2/4 px-1"
						required={true}
						disabled={isCurrentlyInTransit}
					/>

					<FormElement
						{...formElementCommonProps}
						value={formFields.first_name}
						name="first_name"
						id="first_name"
						type="text"
						label={t("firstname", { ns: "user" })}
						className="my-1 w-full sm:w-2/4 px-1"
						required={true}
						disabled={isCurrentlyInTransit}
					/>
				</div>
			)}
			{isCompany && (
				<>
					<FormElement
						{...formElementCommonProps}
						value={formFields.social_reason}
						name="social_reason"
						id="social_reason"
						type="text"
						label={t("raison_sociale", { ns: "user" })}
						className="my-1 px-1"
						required={true}
						disabled={isCurrentlyInTransit}
					/>
				</>
			)}
			<FormElement
				{...formElementCommonProps}
				value={formFields.phone}
				name="phone"
				id="phone"
				type="phone"
				label={t("phone", { ns: "user" })}
				className="px-1"
				required={true}
				disabled={isCurrentlyInTransit}
			/>
			<FormElement
				{...formElementCommonProps}
				value={formFields.mail}
				name="mail"
				type="email"
				id="mail"
				label={t("email", { ns: "user" })}
				className="my-1"
				required={true}
			/>
			<FormElement
				{...formElementCommonProps}
				value={formFields.confirmMail}
				mailToConfirm={formFields.mail}
				name="confirmMail"
				type="email"
				id="confirmMail"
				label={t("confirm_email", { ns: "user" })}
				className="mb-6"
				required={true}
			/>
			{isReady && (
				<div className="flex justify-center my-8">
					<Button
						className={clsx("mx-2 px-4 py-1", {
							"md:px-8 min-w-0": isCurrentlyInTransit,
							"md:px-12": !isCurrentlyInTransit,
						})}
						onClick={() => handleSubmit(ADD_TRANSFER)}
						variant="secondary"
						type="button"
					>
						{t("bike_state.sell_modal_transfer_confirm", {
							ns: "bike",
						})}
					</Button>
					<Button
						onClick={() =>
							isCurrentlyInTransit
								? handleSubmit(CANCEL_TRANSFER)
								: setModalIsOpen(false)
						}
						className="mx-2 px-8 py-1 min-w-0"
						variant="secondaryBordered"
						type="button"
					>
						{isCurrentlyInTransit
							? t("bike_state.sell_modal_transfer_cancel", {
									ns: "bike",
							  })
							: t("global.cancel")}
					</Button>
				</div>
			)}
			{isLoading && (
				<div className="flex justify-center my-8">
					<Spinner color={"var(--primaryColor)"} size={"100px"} />
				</div>
			)}
		</form>
	)
}
